import api from './api';
import { ENDPOINTS } from '../config/api';

export interface WorkflowStep {
  id?: number;
  name: string;
  description?: string;
  order: number;
  approver_type: 'user' | 'role' | 'manager';
  approver_id?: number;
  workflow_id?: number;
}

export interface Workflow {
  id?: number;
  name: string;
  description?: string;
  is_active: boolean;
  leave_type_id?: number;
  steps?: WorkflowStep[];
  created_at?: string;
  updated_at?: string;
}

export interface WorkflowStepRequest {
  name: string;
  description?: string;
  order: number;
  approver_type: 'user' | 'role' | 'manager';
  approver_id?: number;
}

export interface WorkflowRequest {
  name: string;
  description?: string;
  is_active: boolean;
  leave_type_id?: number;
  steps?: WorkflowStepRequest[];
}

export interface WorkflowResponse {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  steps: WorkflowStep[];
  leaveTypes: Array<{
    id: number;
    name: string;
  }>;
}

export interface WorkflowsResponse {
  workflows: WorkflowResponse[];
}

export const workflowService = {
  // Get all workflows
  getWorkflows: async (): Promise<WorkflowResponse[]> => {
    const response = await api.get('/workflows');
    return response.data;
  },

  // Get a single workflow by ID
  getWorkflow: async (id: number): Promise<WorkflowResponse> => {
    const response = await api.get(`/workflows/${id}`);
    return response.data;
  },

  // Create a new workflow
  createWorkflow: async (workflow: WorkflowRequest): Promise<WorkflowResponse> => {
    const response = await api.post('/workflows', workflow);
    return response.data;
  },

  // Update an existing workflow
  updateWorkflow: async (id: number, workflow: Partial<WorkflowRequest>): Promise<WorkflowResponse> => {
    const response = await api.put(`/workflows/${id}`, workflow);
    return response.data;
  },

  // Delete a workflow
  deleteWorkflow: async (id: number): Promise<void> => {
    await api.delete(`/workflows/${id}`);
  },

  // Add a step to workflow
  addWorkflowStep: async (workflowId: number, step: WorkflowStepRequest): Promise<WorkflowStep> => {
    const response = await api.post(`/workflows/${workflowId}/steps`, step);
    return response.data;
  },

  // Update workflow step
  updateWorkflowStep: async (workflowId: number, stepId: number, step: Partial<WorkflowStepRequest>): Promise<WorkflowStep> => {
    const response = await api.put(`/workflows/${workflowId}/steps/${stepId}`, step);
    return response.data;
  },

  // Delete workflow step
  deleteWorkflowStep: async (workflowId: number, stepId: number): Promise<void> => {
    await api.delete(`/workflows/${workflowId}/steps/${stepId}`);
  },

  // Change step order
  reorderWorkflowSteps: async (workflowId: number, stepIds: number[]): Promise<WorkflowStep[]> => {
    const response = await api.post(`/workflows/${workflowId}/steps/reorder`, { step_ids: stepIds });
    return response.data;
  },

  // Assign workflow to leave type
  assignWorkflowToLeaveType: async (workflowId: number, leaveTypeId: number): Promise<void> => {
    await api.post(`/workflows/${workflowId}/leave-types/${leaveTypeId}`);
  },

  // Remove workflow from leave type
  removeWorkflowFromLeaveType: async (workflowId: number, leaveTypeId: number): Promise<void> => {
    await api.delete(`/workflows/${workflowId}/leave-types/${leaveTypeId}`);
  }
};

export default workflowService;